import {
  BaseQueryApi,
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';
import { environment } from '@/environments/environment';
import { LANGUAGE, LanguageHeader } from '@/constants/system';

import Token from '@/utils/token';
import { getRedirectQueryStringInCurrentLocation } from '@/utils/misc.utils';

import { setAccess, setTokens } from '@/store/auth';
import { RootState } from '@/store/rootReducer';
import { addToast, ToastType } from '@/store/system';
import { SessionRenewResponse } from '@/services/api.types';
import { mapRequestError } from '@/services/error.utils';

import i18next from '../i18n';

// Repeated query keys for array values (batch 007 / ADR-A6).
//
// The backend's form decoder walks `url.Values[key]`, so a repeated proto field
// has to arrive as `?status=a&status=b`. fetchBaseQuery's default serialiser
// runs arrays through URLSearchParams, which comma-JOINS them into a single
// value — the server then binds one element with the literal text "a,b", which
// matches nothing. Nothing errors: the caller receives a silently unfiltered
// list. That is why this lives on the shared query rather than in one endpoint.
//
// Non-array values keep the default's exact semantics (undefined stripped,
// everything else String()-ified) so no existing endpoint changes behaviour.
// Batch 006's employee list is unaffected either way — it hand-builds its query
// string precisely because this option did not exist yet.
const repeatedKeyParamsSerializer = (params: Record<string, unknown>) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined) {
      return;
    }
    if (Array.isArray(value)) {
      value.forEach(item => {
        if (item !== undefined) {
          search.append(key, String(item));
        }
      });
      return;
    }
    search.append(key, String(value));
  });
  return search.toString();
};

const authedFetch = fetchBaseQuery({
  baseUrl: environment.apiHost,
  paramsSerializer: repeatedKeyParamsSerializer,
  prepareHeaders: (headers, { getState }) => {
    // Same form as ssobase.query.ts:22-25 — the three query wrappers now index
    // this table identically. The old `LanguageHeader[LanguageTypes[lang]]` double
    // lookup was an identity map for every reachable value ('en'→'en', 'da'→'da')
    // and was two of this file's type errors; behaviour is unchanged.
    const selectedLang = localStorage.getItem(LANGUAGE);
    const headerLang = selectedLang
      ? LanguageHeader[selectedLang as keyof typeof LanguageHeader]
      : LanguageHeader.en;
    headers.set('accept-language', headerLang);
    // `Cloud-Levvel-Tenant` is gone (batch 004, ADR-6) — the new BE resolves the
    // tenant from the request HOST, so the header had no reader. The Bearer was
    // already decoupled from it, which is what made this removal a deletion rather
    // than a rewrite.
    const token = (getState() as RootState).auth.token;
    if (token?.access) {
      headers.set('authorization', `Bearer ${token.access}`);
    }
    headers.set('Cloud-Levvel-Source', 'web');
    return headers;
  },
});

// C0 sliding renewal (ADR-05): the lean JWT carries no refresh. A business 401
// means the terminal access token expired; the FE swaps it for a fresh 1h token
// via POST /session/renew (Bearer = the just-expired token, which is why we reuse
// authedFetch — it attaches the current store token) and replays the original
// request. Single-flight so concurrent 401s trigger exactly one renewal.
const renewMutex = new Mutex();

// End the session: renewal is impossible (past grace / no token / renew rejected /
// BE endpoint not built yet). Clear the token and bounce to login — the full-page
// redirect resets the store, so the stale store.token needs no explicit clear.
const failToLogin = (api: BaseQueryApi) => {
  Token.saveAccessToken('');
  Token.saveRefreshToken('');
  api.dispatch(setAccess(undefined));
  window.location.href = '/login?' + getRedirectQueryStringInCurrentLocation();
};

const authedQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // Wait out any in-flight renewal so a request issued during the renewal window
  // goes out with the fresh token instead of racing a guaranteed 401 (the async-
  // mutex RTK Query re-auth pattern).
  await renewMutex.waitForUnlock();
  let result = await authedFetch(args, api, extraOptions);

  if (result?.error?.status === 401) {
    if (!renewMutex.isLocked()) {
      // Lock holder: perform the single renewal, then replay.
      const release = await renewMutex.acquire();
      try {
        const token = (api.getState() as RootState).auth.token.access;
        if (token) {
          const renewResult = await authedFetch(
            // Empty body {} (not bodyless): the BE Kratos codec requires a
            // Content-Type to decode the request — a bodyless POST 400s with
            // "unregister Content-Type". Passing an object body makes
            // fetchBaseQuery set Content-Type: application/json + serialize {}.
            { url: '/v1/session/renew', method: 'POST', body: {} },
            api,
            extraOptions
          );
          const newToken = (
            renewResult.data as SessionRenewResponse | undefined
          )?.token;
          if (newToken) {
            // Land the fresh token through the same terminal path as login
            // (see login-step.ts). setTokens re-parses the JWT (new exp) and
            // persists it; accessManifest is kept, so /access is not re-fetched.
            api.dispatch(setTokens({ access: newToken, refresh: '' }));
            result = await authedFetch(args, api, extraOptions);
          } else {
            // Renew rejected — only the lock holder ends the session, so waiters
            // (below) don't each fire a redundant redirect.
            failToLogin(api);
          }
        } else {
          failToLogin(api);
        }
      } finally {
        release();
      }
    } else {
      // Waiter: another request is renewing. Wait for it, then replay with the
      // fresh token. If the renewal failed, this replay 401s and returns as-is —
      // harmless, the lock holder's full-page redirect overrides it.
      await renewMutex.waitForUnlock();
      result = await authedFetch(args, api, extraOptions);
    }
  }

  switch (result?.error?.status) {
    case 500: {
      // Kratos error shape (DEF-005-01, batch 004 / ADR-1). This read used to be
      // `(result?.error?.data as any)?.error?.detail` — the legacy BFF envelope,
      // which the `as any` hid from both the compiler and a naive grep. Against
      // the new BE it is always undefined, so `i18next.t(undefined)` rendered an
      // EMPTY string: users got a titled toast with no body on every 500.
      //
      // mapRequestError (profile.utils.ts) is the single kratos reader 005
      // established; reusing it is deliberate, because duplicating shape knowledge
      // per call site is precisely how the legacy envelope ended up in 20+ files.
      // A 500 carries no attributable field, so this resolves to the generic copy.
      const { i18nKey, text } = mapRequestError(result.error);
      console.error(result);
      api.dispatch(
        addToast({
          msg: i18nKey ? i18next.t(i18nKey) : text ?? '',
          title: i18next.t('cdb_message_status_error_text'),
          type: ToastType.Danger,
        })
      );
      break;
    }
    case 'FETCH_ERROR':
      window.location.href = '/login';
      break;
    default:
      break;
  }
  return result;
};

export default authedQuery;
